import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MdHowToVote, MdPeople, MdBarChart, MdSecurity, MdEmojiEvents, MdAssessment, MdVerifiedUser, MdForum, MdEmail, MdExpandMore } from 'react-icons/md';
import ContactForm from '../components/ContactForm';

const FAQ_ITEMS = [
  {
    question: 'Is Civote free to use?',
    answer: 'Yes! Creating an account, voting on polls, earning points, and viewing representative profiles are completely free. We only charge for advanced features like the Candidacy Assessment and Representative subscriptions.',
  },
  {
    question: 'How does the voting process work?',
    answer: 'Browse polls on topics that matter to you, read the question, and select your response. Each vote earns you Poll Points that unlock badges and help you climb the engagement leaderboard. Your individual responses are always anonymous.',
  },
  {
    question: 'Is my vote anonymous?',
    answer: 'Absolutely. Your individual poll responses are never tied to your public profile. We only display aggregated results and demographic breakdowns — no one can see how you personally voted.',
  },
  {
    question: 'How do I become a representative on Civote?',
    answer: 'Start by creating a free account and completing the Candidacy Assessment to see how your views align with voters in your area. From there you can set up a public representative profile, subscribe to a plan, and start engaging with your community.',
  },
  {
    question: 'What is the Candidacy Assessment?',
    answer: 'The Candidacy Assessment is a paid, one-time report that analyzes your poll responses against voters in your state and district. It breaks down your alignment by topic area, giving you data-driven insight into where you stand relative to your potential constituents.',
  },
  {
    question: 'Can I create my own polls?',
    answer: 'Yes! Any registered user can create custom polls. Submitted polls go through a brief review process to ensure quality, then they are published for the community to vote on.',
  },
  {
    question: 'How do Poll Points and badges work?',
    answer: 'You earn Poll Points every time you vote. As your points accumulate you unlock tier badges — from Newcomer all the way up to Poll Titan. Streaks, daily participation, and other activities award bonus points.',
  },
  {
    question: 'How do I contact support?',
    answer: 'Use the contact form below, or if you already have an account, visit the Help & Support page from the main menu. We typically respond within one business day.',
  },
];

function FaqAccordion() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div className="space-y-3" data-testid="faq-accordion">
      {FAQ_ITEMS.map((item, i) => (
        <div
          key={i}
          className="border border-gray-700 rounded-xl overflow-hidden transition-colors hover:border-gray-600"
          data-testid={`faq-item-${i}`}
        >
          <button
            onClick={() => toggle(i)}
            className="w-full flex items-center justify-between px-5 py-4 text-left focus:outline-none"
            data-testid={`faq-toggle-${i}`}
          >
            <span className="text-white font-medium font-rubik pr-4">{item.question}</span>
            <MdExpandMore
              className={`text-gray-400 text-xl flex-shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180' : ''}`}
            />
          </button>
          <div
            className={`grid transition-all duration-200 ${openIndex === i ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
          >
            <div className="overflow-hidden">
              <p className="px-5 pb-4 text-gray-400 font-rubik text-sm leading-relaxed">{item.answer}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ContactAccordion() {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="mt-3 border border-gray-700 rounded-xl overflow-hidden transition-colors hover:border-gray-600"
      data-testid="contact-accordion"
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left focus:outline-none"
        data-testid="contact-accordion-toggle"
      >
        <span className="text-white font-medium font-rubik flex items-center gap-2">
          <MdEmail className="text-primary" /> Still have questions? Send us a message.
        </span>
        <MdExpandMore
          className={`text-gray-400 text-xl flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div className={`grid transition-all duration-200 ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden">
          <div className="px-5 pb-5">
            <ContactForm variant="dark" />
          </div>
        </div>
      </div>
    </div>
  );
}

function LandingPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      navigate('/home');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-900/80 backdrop-blur-lg border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_votesphere-6/artifacts/suvvb8kr_App%20icon.png" 
                alt="CIVOTE" 
                className="h-10 w-10 rounded-lg"
              />
              <span className="text-2xl font-bold text-white font-rubik">CIVOTE</span>
            </div>
            <div className="flex items-center gap-4">
              <Link 
                to="/login" 
                className="px-4 py-2 text-gray-300 hover:text-white font-medium font-rubik transition-colors"
                data-testid="landing-login-btn"
              >
                Login
              </Link>
              <Link 
                to="/login?register=true" 
                className="px-6 py-2 bg-primary hover:bg-primary-dark text-white font-semibold rounded-full font-rubik transition-all transform hover:scale-105"
                data-testid="landing-register-btn"
              >
                Register
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-primary/20"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-green-500/20 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white font-rubik mb-6 leading-tight">
            Your Voice <span className="text-primary">Matters</span>
          </h1>
          <p className="text-xl sm:text-2xl text-gray-300 font-rubik mb-10 max-w-3xl mx-auto">
            The civic engagement platform that connects you to your representatives, 
            tracks your alignment on real issues, and rewards you for participating.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/login?register=true" 
              className="px-8 py-4 bg-primary hover:bg-primary-dark text-white text-lg font-bold rounded-full font-rubik transition-all transform hover:scale-105 shadow-lg shadow-primary/30"
              data-testid="hero-get-started-btn"
            >
              Get Started Free
            </Link>
            <Link 
              to="/login" 
              className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white text-lg font-semibold rounded-full font-rubik transition-all border border-white/20"
              data-testid="hero-login-btn"
            >
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* Value Propositions */}
      <section className="py-16 px-4 bg-gray-800/50" data-testid="value-props-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/20 rounded-full mb-4">
                <MdHowToVote className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-white font-rubik mb-2">Vote on Real Issues</h3>
              <p className="text-gray-400 font-rubik text-sm">
                Weigh in on curated civic topics and community-created polls that shape the political conversation.
              </p>
            </div>
            
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/20 rounded-full mb-4">
                <MdAssessment className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white font-rubik mb-2">Know Where You Stand</h3>
              <p className="text-gray-400 font-rubik text-sm">
                Get a Candidacy Assessment that shows how your views align with voters in your area, broken down by topic.
              </p>
            </div>
            
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-500/20 rounded-full mb-4">
                <MdVerifiedUser className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="text-xl font-bold text-white font-rubik mb-2">Connect with Representatives</h3>
              <p className="text-gray-400 font-rubik text-sm">
                Find your elected officials, compare match percentages, and hold them accountable on the issues you care about.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-rubik mb-4">
              Why Choose Civote?
            </h2>
            <p className="text-gray-400 font-rubik text-lg max-w-2xl mx-auto">
              A powerful platform designed to amplify voices and foster meaningful civic engagement
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="p-6 rounded-2xl bg-gray-800/50 border border-gray-700 hover:border-primary/50 transition-all group">
              <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/30 transition-colors">
                <MdHowToVote className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-white font-rubik mb-2">Vote on What Matters</h3>
              <p className="text-gray-400 font-rubik">
                Participate in polls on topics that affect your community, from local issues to national debates.
              </p>
            </div>
            
            <div className="p-6 rounded-2xl bg-gray-800/50 border border-gray-700 hover:border-primary/50 transition-all group">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-500/30 transition-colors">
                <MdBarChart className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white font-rubik mb-2">Demographic Insights</h3>
              <p className="text-gray-400 font-rubik">
                See how different groups respond to polls with detailed demographic breakdowns and filters.
              </p>
            </div>
            
            <div className="p-6 rounded-2xl bg-gray-800/50 border border-gray-700 hover:border-primary/50 transition-all group">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-500/30 transition-colors">
                <MdPeople className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-xl font-bold text-white font-rubik mb-2">Create Your Own Polls</h3>
              <p className="text-gray-400 font-rubik">
                Start conversations that matter to you by creating custom polls for the community.
              </p>
            </div>
            
            <div className="p-6 rounded-2xl bg-gray-800/50 border border-gray-700 hover:border-primary/50 transition-all group">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-green-500/30 transition-colors">
                <MdSecurity className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-white font-rubik mb-2">Secure & Private</h3>
              <p className="text-gray-400 font-rubik">
                Your responses are anonymous. We protect your privacy while enabling meaningful participation.
              </p>
            </div>
            
            <div className="p-6 rounded-2xl bg-gray-800/50 border border-gray-700 hover:border-primary/50 transition-all group">
              <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-yellow-500/30 transition-colors">
                <MdEmojiEvents className="w-6 h-6 text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-white font-rubik mb-2">Earn Rewards</h3>
              <p className="text-gray-400 font-rubik">
                Collect Poll Points, unlock badges, build streaks, and climb from Newcomer to Poll Titan.
              </p>
            </div>
            
            <div className="p-6 rounded-2xl bg-gray-800/50 border border-gray-700 hover:border-primary/50 transition-all group">
              <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-pink-500/30 transition-colors">
                <MdForum className="w-6 h-6 text-pink-400" />
              </div>
              <h3 className="text-xl font-bold text-white font-rubik mb-2">Candidacy Assessment</h3>
              <p className="text-gray-400 font-rubik">
                Thinking about running for office? See how your positions align with your potential constituents.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-gray-800/50" data-testid="landing-faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-rubik mb-3">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-400 font-rubik text-lg">
              Everything you need to know before getting started
            </p>
          </div>
          <FaqAccordion />
        </div>
      </section>

      {/* Contact Us Section */}
      <section className="py-16 px-4" id="contact" data-testid="landing-contact-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <MdEmail className="text-3xl text-primary" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white font-rubik mb-2">
              Contact Us
            </h2>
            <p className="text-gray-400 font-rubik">
              Have a question that isn&apos;t covered above? Drop us a line.
            </p>
          </div>
          <ContactAccordion />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_votesphere-6/artifacts/suvvb8kr_App%20icon.png" 
                  alt="CIVOTE" 
                  className="h-10 w-10 rounded-lg"
                />
                <span className="text-2xl font-bold text-white font-rubik">CIVOTE</span>
              </div>
              <p className="text-gray-400 font-rubik max-w-md">
                Empowering civic engagement through accessible polling and community-driven conversations.
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold font-rubik mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/login" className="text-gray-400 hover:text-primary font-rubik transition-colors">
                    Login
                  </Link>
                </li>
                <li>
                  <Link to="/login?register=true" className="text-gray-400 hover:text-primary font-rubik transition-colors">
                    Register
                  </Link>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold font-rubik mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/terms" className="text-gray-400 hover:text-primary font-rubik transition-colors" data-testid="footer-terms-link">
                    Terms & Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-gray-400 hover:text-primary font-rubik transition-colors" data-testid="footer-privacy-link">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-500 font-rubik text-sm">
              &copy; {new Date().getFullYear()} Civote. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
